import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Input, Upload, Modal, Select, Popconfirm } from "antd";
import {
  UploadOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import html2pdf from "html2pdf.js";
import api from "../Interceptor";
import { getRoute } from "../api/routes";
import useModal from "../hooks/useModal";
import { CustomInput, CustomSelect } from "../components/ui";

const { TextArea } = Input;
const { Option } = Select;

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8011";

const InvoiceDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const id = location.state?.invoiceId;
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saveLoader, setSaveLoader] = useState(false);
  const invoiceRef = useRef();

  // Form states
  const [formData, setFormData] = useState({
    number: "",
    customerName: "",
    customerCompany: "",
    date: "",
    currency: "",
    discount: 0,
    note: "",
    taxes: [],
    items: [],
  });

  const [currencies, setCurrencies] = useState([]);
  const [taxName, setTaxName] = useState("");
  const [taxRate, setTaxRate] = useState("");
  const [itemName, setItemName] = useState("");
  const [itemQuantity, setItemQuantity] = useState(1);
  const [itemUnitPrice, setItemUnitPrice] = useState(0);
  const [isTaxModalVisible, setIsTaxModalVisible] = useState(false);
  const [isItemModalVisible, setIsItemModalVisible] = useState(false);

  const companyName = localStorage.getItem("companyName");
  const companyLogo = localStorage.getItem("companyLogo");
  const companySignature = localStorage.getItem("companySignature");
  const companyAbout = localStorage.getItem("companyAbout");
  const [companyInfo] = useState(
    !!(companyName && companyLogo && companyAbout && companySignature),
  );
  const { isOpen, toggleModal } = useModal();

  // Fetch invoice data
  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const endpoint = `${getRoute(
          "users",
          "admin",
          "invoices",
          false,
        )}?id=${id}`;
        const response = await api.get(endpoint);
        const invoiceData = response.data;

        if (invoiceData) {
          setInvoice(invoiceData);
          setFormData({
            number: invoiceData.number || "",
            customerName: invoiceData.customerName || "",
            customerCompany: invoiceData.customerCompany || "",
            date: invoiceData.date ? invoiceData.date.split("T")[0] : "",
            currency: invoiceData.currency?._id || "",
            discount: invoiceData.discount || 0,
            note: invoiceData.note || "",
            taxes: invoiceData.taxes || [],
            items: invoiceData.items || [],
          });
        } else {
          toast.error("Invoice not found for this company");
        }
        setLoading(false);
      } catch (error) {
        console.error(error);
        toast.error(
          error.response?.data?.message || "Failed to fetch invoice details",
        );
        setLoading(false);
      }
    };

    if (id) {
      fetchInvoice();
    }
  }, [id]);

  // Fetch currencies
  const getAllCurrencies = async () => {
    try {
      const { data: currencyData } = await api.get(
        `${API_BASE_URL}/api/v1/getAllCurrencies`,
      );
      setCurrencies(currencyData);
    } catch (error) {
      if (error?.isUnpaid) return;
      toast.error("Oops! Error while fetching Currencies");
    }
  };

  useEffect(() => {
    getAllCurrencies();
  }, []);

  const handleDownload = () => {
    const element = invoiceRef.current;
    const options = {
      margin: 0.5,
      filename: `Invoice_${formData.customerName}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };
    html2pdf().from(element).set(options).save();
  };

  const handleEditToggle = () => {
    setEditMode(!editMode);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddTax = () => {
    if (!taxName || !taxRate) return;
    const newTax = { name: taxName, rate: parseFloat(taxRate) };
    setFormData((prev) => ({
      ...prev,
      taxes: [...prev.taxes, newTax],
    }));
    setTaxName("");
    setTaxRate("");
    setIsTaxModalVisible(false);
  };

  const handleAddItem = () => {
    if (!itemName || itemQuantity <= 0 || itemUnitPrice <= 0) return;
    const newItem = {
      name: itemName,
      quantity: Number(itemQuantity),
      unitPrice: Number(itemUnitPrice),
      totalPrice: Number(itemQuantity) * Number(itemUnitPrice),
    };
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
    setItemName("");
    setItemQuantity(1);
    setItemUnitPrice(0);
    setIsItemModalVisible(false);
  };

  const handleDeleteTax = (index) => {
    setFormData((prev) => ({
      ...prev,
      taxes: prev.taxes.filter((_, i) => i !== index),
    }));
  };

  const handleDeleteItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const uploadImage = async (image) => {
    const data = new FormData();
    data.append("file", image);
    data.append("upload_preset", "ExpenseTracker");
    try {
      const response = await fetch(
        "https://api.cloudinary.com/v1_1/dqvgbgje8/image/upload",
        {
          method: "PUT",
          body: data,
        },
      );
      const urlData = await response.json();
      return urlData.url;
    } catch (error) {
      console.error(error);
      toast.error("Image upload failed.");
      return null;
    }
  };

  const handleSave = async () => {
    setSaveLoader(true);

    const updatedInvoice = {
      id,
      number: formData.number,
      customerName: formData.customerName,
      customerCompany: formData.customerCompany,
      date: new Date(formData.date).toISOString(),
      currency: formData.currency,
      discount: formData.discount || undefined,
      note: formData.note || undefined,
      taxes: formData.taxes.map((tax) => ({
        name: tax.name,
        rate: tax.rate,
      })),
      items: formData.items.map((item) => ({
        name: item.name,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
      })),
    };

    try {
      const endpoint = getRoute("users", "admin", "invoices", false);
      await api.put(endpoint, updatedInvoice);
      toast.success("Invoice updated successfully!");
      setEditMode(false);
      // Refresh the invoice data
      const response = await api.get(
        `${getRoute("users", "admin", "invoices", false)}?id=${id}`,
      );
      setInvoice(response.data);
    } catch (error) {
      toast.error("Failed to update invoice");
    } finally {
      setSaveLoader(false);
    }
  };

  const handleCancel = () => {
    if (invoice) {
      setFormData({
        number: invoice.number || "",
        customerName: invoice.customerName || "",
        customerCompany: invoice.customerCompany || "",
        date: invoice.date ? invoice.date.split("T")[0] : "",
        currency: invoice.currency?._id || "",
        discount: invoice.discount || 0,
        note: invoice.note || "",
        taxes: invoice.taxes || [],
        items: invoice.items || [],
      });
    }
    setEditMode(false);
  };

  const calculateInvoiceTotal = () => {
    const subtotal = formData.items.reduce(
      (total, item) => total + parseFloat(item.totalPrice || 0),
      0,
    );
    const taxAmount = formData.taxes.reduce(
      (total, tax) => total + (subtotal * parseFloat(tax.rate || 0)) / 100,
      0,
    );
    const discountAmount = formData.discount
      ? (subtotal * formData.discount) / 100
      : 0;
    const total = subtotal + taxAmount - discountAmount;

    return { subtotal, taxAmount, discountAmount, total };
  };
  useEffect(() => {
    if (location.state?.editMode === true) {
      setEditMode(true);
    }
  }, [location.state]);
  if (loading)
    return (
      <div className="flex justify-center items-center h-full">
        <div className="spinner"></div>
      </div>
    );

  if (!invoice) return <div>Invoice not found</div>;

  const { subtotal, taxAmount, discountAmount, total } =
    calculateInvoiceTotal();
  const currencySymbol =
    currencies.find((c) => c._id === formData.currency)?.symbol || "$";

  return (
    <div className="bg-white p-3">
      {!companyInfo && (
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-2xl shadow-sm">
          <div className="flex items-center justify-center min-w-6 h-6 rounded-full bg-blue-100 text-blue-600 font-bold">
            i
          </div>
          <p className="text-sm md:text-base">
            Add{" "}
            <span
              className="ml-1 font-semibold underline cursor-pointer"
              role="button"
              onClick={toggleModal}
            >
              Company Info
            </span>{" "}
            to populate in invoices.
          </p>
        </div>
      )}

      <div className="min-h-screen flex flex-col p-3 gap-2 overflow-hidden font-customFont">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div style={{ fontFamily: "Noto Serif, sans-serif" }}>
            <h1 className="font-bold text-lg md:text-xl">Invoice</h1>
            <p className="text-xs text-gray-500 font-semibold">
              {editMode ? "Edit invoice details" : "View or Download invoice"}
            </p>
          </div>
          <div className="flex gap-3 items-center mt-2 md:mt-0">
            {editMode ? (
              <>
                <button
                  className="border border-gray-300 px-4 py-1 rounded-md"
                  onClick={handleCancel}
                  disabled={saveLoader}
                >
                  Cancel
                </button>
                <button
                  type="primary"
                  className="bg-themeGradient text-white px-4 py-1 rounded-md"
                  onClick={handleSave}
                  loading={saveLoader}
                >
                  Update
                </button>
              </>
            ) : (
              <>
                <button
                  className="bg-themeGradient text-white hover:bg-themeGradientHover px-3 py-1 rounded-md flex items-center justify-center w-full md:w-auto"
                  onClick={handleEditToggle}
                >
                  Edit Invoice
                </button>
                <button
                  className="text-black border border-[#DCE3E3] px-4 py-1 rounded-md flex items-center"
                  onClick={handleDownload}
                >
                  <img
                    src="/icons/Download.svg"
                    className="w-4 mr-2"
                    alt="Download"
                  />
                  Download
                </button>
              </>
            )}
          </div>
        </div>

        <div ref={invoiceRef}>
          {/* Company Header */}
          <div className="text-center mb-4">
            <h2 className="text-2xl font-bold">{companyName}</h2>
            <h2 className="text-lg font-semibold text-[#535862]">
              {companyAbout}
            </h2>
          </div>

          {/* Invoice Details Section */}
          <div className="flex justify-between me-10 my-4">
            <div className="flex-1">
              {editMode ? (
                <>
                  <div className="my-4">
                    <label className="block font-semibold mb-1">
                      Invoice #
                    </label>
                    <Input
                      type="number"
                      value={formData.number}
                      onChange={(e) =>
                        handleInputChange("number", e.target.value)
                      }
                      className="w-64"
                    />
                  </div>
                  <div className="my-4">
                    <label className="block font-semibold mb-1">Company:</label>
                    <Input
                      value={formData.customerCompany}
                      onChange={(e) =>
                        handleInputChange("customerCompany", e.target.value)
                      }
                      className="w-64"
                    />
                  </div>
                  <div className="my-4">
                    <label className="block font-semibold mb-1">
                      Customer Name:
                    </label>
                    <Input
                      value={formData.customerName}
                      onChange={(e) =>
                        handleInputChange("customerName", e.target.value)
                      }
                      className="w-64"
                    />
                  </div>
                  <div className="my-4">
                    <label className="block font-semibold mb-1">
                      Invoice Date:
                    </label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) =>
                        handleInputChange("date", e.target.value)
                      }
                      className="w-64"
                    />
                  </div>
                  <div className="my-4">
                    <label className="block font-semibold mb-1">
                      Currency:
                    </label>
                    <Select
                      value={formData.currency}
                      onChange={(value) => handleInputChange("currency", value)}
                      className="w-64"
                    >
                      {currencies.map((currency) => (
                        <Option key={currency._id} value={currency._id}>
                          {currency.symbol} {currency.name}
                        </Option>
                      ))}
                    </Select>
                  </div>
                  <div className="my-4">
                    <label className="block font-semibold mb-1">
                      Discount (%):
                    </label>
                    <Input
                      type="number"
                      value={formData.discount}
                      onChange={(e) =>
                        handleInputChange("discount", e.target.value)
                      }
                      className="w-64"
                    />
                  </div>
                </>
              ) : (
                <>
                  {formData.number && (
                    <div className="my-4">
                      <strong className="me-2">Invoice #</strong>
                      <span className="text-[#535862]">{formData.number}</span>
                    </div>
                  )}
                  {formData.customerCompany && (
                    <div className="my-4">
                      <strong className="me-2">Company:</strong>
                      <span className="text-[#535862]">
                        {formData.customerCompany}
                      </span>
                    </div>
                  )}
                  {formData.customerName && (
                    <div className="my-4">
                      <strong className="me-2">Customer Name:</strong>
                      <span className="text-[#535862]">
                        {formData.customerName}
                      </span>
                    </div>
                  )}
                  <div className="my-4">
                    <strong className="me-2">Invoice Date:</strong>
                    <span className="text-[#535862]">
                      {new Date(formData.date).toLocaleDateString()}
                    </span>
                  </div>
                </>
              )}
            </div>
            <div>
              {companyLogo ? (
                <img
                  src={companyLogo}
                  className="w-36 h-36"
                  alt="Company Logo"
                />
              ) : (
                <div className="border p-2 w-20 text-gray-400">
                  Add company info to populate
                </div>
              )}
            </div>
          </div>

          {/* Items Section */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-bold text-lg">Items</h3>
              {editMode && (
                <Button onClick={() => setIsItemModalVisible(true)}>
                  Add Item <PlusOutlined />
                </Button>
              )}
            </div>

            <div className="overflow-x-auto mt-2 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 rounded-2xl border">
                <thead className="bg-[#E9EAEB] text-[#535862]">
                  <tr>
                    <th className="px-4 py-3">Item Name</th>
                    <th className="px-4 py-3">Quantity</th>
                    <th className="px-4 py-3">Unit Price</th>
                    <th className="px-4 py-3">Total Price</th>
                    {editMode && <th className="px-4 py-3">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item, index) => (
                    <tr key={index} className="border-b text-center">
                      <td className="px-4 py-3">
                        {editMode ? (
                          <Input
                            value={item.name}
                            onChange={(e) => {
                              const newItems = [...formData.items];
                              newItems[index].name = e.target.value;
                              handleInputChange("items", newItems);
                            }}
                          />
                        ) : (
                          item.name
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {editMode ? (
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => {
                              const newItems = [...formData.items];
                              newItems[index].quantity = Number(e.target.value);
                              newItems[index].totalPrice =
                                newItems[index].quantity *
                                newItems[index].unitPrice;
                              handleInputChange("items", newItems);
                            }}
                          />
                        ) : (
                          item.quantity
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {editMode ? (
                          <Input
                            type="number"
                            value={item.unitPrice}
                            onChange={(e) => {
                              const newItems = [...formData.items];
                              newItems[index].unitPrice = Number(
                                e.target.value,
                              );
                              newItems[index].totalPrice =
                                newItems[index].quantity *
                                newItems[index].unitPrice;
                              handleInputChange("items", newItems);
                            }}
                          />
                        ) : (
                          `${currencySymbol} ${parseFloat(item.unitPrice)
                            .toFixed(2)
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {currencySymbol}{" "}
                        {parseFloat(item.totalPrice)
                          .toFixed(2)
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      </td>
                      {editMode && (
                        <td className="px-4 py-3">
                          <Popconfirm
                            title="Are you sure you want to delete this item?"
                            onConfirm={() => handleDeleteItem(index)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <DeleteOutlined className="text-red-500 cursor-pointer" />
                          </Popconfirm>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Taxes Section */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-bold text-lg">Taxes</h3>
              {editMode && (
                <Button onClick={() => setIsTaxModalVisible(true)}>
                  Add Tax <PlusOutlined />
                </Button>
              )}
            </div>

            <div className="overflow-x-auto mt-2 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 rounded-2xl border">
                <thead className="bg-[#E9EAEB] text-[#535862]">
                  <tr>
                    <th className="px-4 py-3">Tax Name</th>
                    <th className="px-4 py-3">Tax Rate (%)</th>
                    {editMode && <th className="px-4 py-3">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {formData.taxes.map((tax, index) => (
                    <tr key={index} className="border-b text-center">
                      <td className="px-4 py-3">
                        {editMode ? (
                          <Input
                            value={tax.name}
                            onChange={(e) => {
                              const newTaxes = [...formData.taxes];
                              newTaxes[index].name = e.target.value;
                              handleInputChange("taxes", newTaxes);
                            }}
                          />
                        ) : (
                          tax.name
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {editMode ? (
                          <Input
                            type="number"
                            value={tax.rate}
                            onChange={(e) => {
                              const newTaxes = [...formData.taxes];
                              newTaxes[index].rate = e.target.value;
                              handleInputChange("taxes", newTaxes);
                            }}
                          />
                        ) : (
                          tax.rate
                        )}
                      </td>

                      {editMode && (
                        <td className="px-4 py-3">
                          <Popconfirm
                            title="Are you sure you want to delete this tax?"
                            onConfirm={() => handleDeleteTax(index)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <DeleteOutlined className="text-red-500 cursor-pointer" />
                          </Popconfirm>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes Section */}
          <div className="mb-4">
            <label className="font-bold text-lg">Note:</label>
            {editMode ? (
              <TextArea
                value={formData.note}
                onChange={(e) => handleInputChange("note", e.target.value)}
                rows={4}
                className="mt-2"
              />
            ) : (
              <div className="mt-2">
                <span style={{ whiteSpace: "pre-line" }}>
                  {formData.note || "No notes"}
                </span>
              </div>
            )}
          </div>

          {/* Summary Section */}
          <div className="flex flex-col md:flex-row justify-between mx-4 md:mx-5 lg:mx-10 mt-8 gap-6">
            {/* Company Signature */}
            {!editMode && (
              <div className="w-full md:w-44 flex justify-center md:justify-start mt-5 order-2 md:order-1">
                {companySignature ? (
                  <img
                    src={companySignature}
                    className="w-36 h-36 object-contain"
                    alt="Company Logo"
                  />
                ) : (
                  <div className="w-full md:w-44 h-32 border flex items-center justify-center text-gray-400">
                    No Signature
                  </div>
                )}
              </div>
            )}

            {/* Totals Section */}
            <div className="w-full md:w-auto flex flex-col md:ml-auto order-1 md:order-2">
              <div className="flex justify-between w-full md:w-64 mb-2">
                <strong>Subtotal:</strong>
                <span>
                  {currencySymbol}{" "}
                  {subtotal.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                </span>
              </div>

              {formData.taxes.map((tax, index) => (
                <div
                  key={index}
                  className="flex justify-between w-full md:w-64 mb-2"
                >
                  <strong>{tax.name}:</strong>
                  <span>{tax.rate}%</span>
                </div>
              ))}

              {taxAmount > 0 && (
                <div className="flex justify-between w-full md:w-64 mb-2">
                  <strong>Tax Amount:</strong>
                  <span>
                    {currencySymbol}{" "}
                    {taxAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </span>
                </div>
              )}

              {formData.discount > 0 && (
                <div className="flex justify-between w-full md:w-64 mb-2">
                  <strong>Discount ({formData.discount}%):</strong>
                  <span>
                    - {currencySymbol}{" "}
                    {discountAmount
                      .toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </span>
                </div>
              )}

              <div className="flex justify-between text-lg w-full md:w-64 border-t border-[#535862] pt-2 mt-2">
                <strong>Total:</strong>
                <strong>
                  {currencySymbol}{" "}
                  {total.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                </strong>
              </div>
            </div>
          </div>

          {/* Footer */}
          {/* <div className="justify-end flex flex-col items-end mt-20 mb-5">
            <div className="text-left text-xs text-[#535862]">
              <div>
                <strong>DG-Expense</strong>
              </div>
              <div>
                Powered by <strong>Devsouq Technologies</strong>
              </div>
            </div>
          </div> */}
        </div>
      </div>

      {/* Modals for adding taxes and items */}
      <Modal
        title="Add Tax"
        open={isTaxModalVisible}
        onCancel={() => setIsTaxModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsTaxModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleAddTax}>
            Add Tax
          </Button>,
        ]}
      >
        <div className="space-y-4">
          <div>
            <label className="block mb-1">Tax Name</label>
            <Input
              value={taxName}
              onChange={(e) => setTaxName(e.target.value)}
              placeholder="Enter tax name"
            />
          </div>
          <div>
            <label className="block mb-1">Tax Rate (%)</label>
            <Input
              type="number"
              value={taxRate}
              onChange={(e) => setTaxRate(e.target.value)}
              placeholder="Enter tax rate"
            />
          </div>
        </div>
      </Modal>

      <Modal
        title="Add Item"
        open={isItemModalVisible}
        onCancel={() => setIsItemModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsItemModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleAddItem}>
            Add Item
          </Button>,
        ]}
      >
        <div className="space-y-4">
          <div>
            <label className="block mb-1">Item Name</label>
            <Input
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              placeholder="Enter item name"
            />
          </div>
          <div>
            <label className="block mb-1">Quantity</label>
            <Input
              type="number"
              value={itemQuantity}
              onChange={(e) => setItemQuantity(e.target.value)}
              placeholder="Enter quantity"
            />
          </div>
          <div>
            <label className="block mb-1">Unit Price</label>
            <Input
              type="number"
              value={itemUnitPrice}
              onChange={(e) => setItemUnitPrice(e.target.value)}
              placeholder="Enter unit price"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InvoiceDetail;
